import { noteName } from '@muzly/voice-engine';
import type {
  BreathSegment,
  FachKey,
  MetricKey,
  PitchRegister,
  PitchRegisterId,
  RangeInfo,
  Vibrato,
} from '@muzly/voice-engine';

import { DIAGNOSIS_SCHEMA_VERSION, STORED_METRIC_KEYS } from '../record';

import type {
  AnalyBreathSegment,
  AnalyPitchRegister,
  AnalySongPartScore,
  AnalySongScore,
  AnalyTerrain,
  AnalyVibrato,
  AnalyView,
} from './analyContract';
import type { CaptureSource } from '../capture';
import type { OnboardingAnswer } from '../flow';
import type { DiagnosisRecord } from '../record';
import type {
  PitchTendency,
  SongScore,
  SongSegmentScore,
} from '../songScoring';

/**
 * fromAnalyView — 서버 이력 한 회차(`AnalyView`) → 로컬 `DiagnosisRecord`.
 *
 * `toDiagnosisRecord`의 역방향이지만 입력이 다르다: 저쪽은 엔진 `Report`를 받고 이쪽은
 * 서버가 이미 합성해 둔 값(`terrain$`·`range$`·`metrics$`)을 받는다. 서버가 가진 적 없는
 * 것(컨디션·환경·`registerBreaks`·녹음)은 채우지 않는다 — `undefined`가 아니라 필드
 * 자체가 없다("Measured vs not measured").
 *
 * **필수 아니면 생략**: `DiagnosisRecord`의 옵셔널 필드 대부분은 서버 쪽 대응 필드도
 * 옵셔널(`AnalySustainMetrics`·`AnalyVibrato`·`AnalyBreathSegment` 등)이라, 부분적으로만
 * 채워진 값(예: `vibrato.rateHz`만 있고 `depthCents`가 없음)은 **그 필드 전체를 뺀다** —
 * 반쪽짜리 `Vibrato`를 만들 수 없다(엔진 타입은 세 필드 다 필수).
 *
 * 순수 함수. React·저장소 IO 의존 없음 — vitest 대상(Seams).
 */

const FACH_KEYS: readonly FachKey[] = [
  'soprano',
  'alto',
  'tenor',
  'bass',
  'soprano_tenor',
  'alto_bass',
];

const PITCH_REGISTER_IDS: readonly PitchRegisterId[] = [
  'lowExtension',
  'modal',
  'passaggio',
  'head',
  'highExtension',
];

const SONG_TENDENCIES: readonly PitchTendency[] = ['flat', 'sharp', 'onPitch'];

/** `<source>-v<n>` 접두에서 캡처 경로만 꺼낸다. 없거나 모르는 접두는 `native`로 본다. */
const sourceOf = (algoVersion: string | undefined): CaptureSource => {
  const prefix = algoVersion?.split('-')[0];
  return prefix === 'mic' || prefix === 'mock' ? prefix : 'native';
};

/** 축→점수 맵(`AnalyTerrain`)에서 앱이 저장하는 4축만, 있는 것만 꺼낸다. */
const toScores = (
  terrain: AnalyTerrain | undefined,
): { key: MetricKey; val: number }[] =>
  STORED_METRIC_KEYS.flatMap((key) => {
    const val = terrain?.[key];
    return val === undefined ? [] : [{ key, val }];
  });

/** 세 필드가 다 있어야 `Vibrato`다 — 하나라도 없으면 통째로 없는 것으로 본다. */
const toVibrato = (v: AnalyVibrato | undefined): Vibrato | undefined =>
  v?.rateHz !== undefined &&
  v.depthCents !== undefined &&
  v.regularityPct !== undefined
    ? {
        rateHz: v.rateHz,
        depthCents: v.depthCents,
        regularityPct: v.regularityPct,
      }
    : undefined;

const toBreath = (
  segments: AnalyBreathSegment[] | undefined,
): BreathSegment[] | undefined => {
  const valid = (segments ?? []).flatMap((s) =>
    s.id !== undefined && s.score !== undefined
      ? [{ id: s.id, score: s.score }]
      : [],
  );
  return valid.length > 0 ? valid : undefined;
};

const toPitchRegisters = (
  regs: AnalyPitchRegister[] | undefined,
): PitchRegister[] | undefined => {
  const valid = (regs ?? []).flatMap((r) =>
    r.id !== undefined &&
    PITCH_REGISTER_IDS.includes(r.id as PitchRegisterId) &&
    r.lowMidi !== undefined &&
    r.highMidi !== undefined
      ? [
          {
            id: r.id as PitchRegisterId,
            lowMidi: r.lowMidi,
            highMidi: r.highMidi,
            ...(r.score !== undefined ? { score: r.score } : {}),
          },
        ]
      : [],
  );
  return valid.length > 0 ? valid : undefined;
};

const toSongPartScore = (
  part: AnalySongPartScore | undefined,
): SongSegmentScore | undefined => {
  if (part?.sungNotes === undefined || part.totalNotes === undefined) {
    return undefined;
  }
  const tendency =
    typeof part.tendency === 'string' &&
    SONG_TENDENCIES.includes(part.tendency as PitchTendency)
      ? (part.tendency as PitchTendency)
      : undefined;
  return {
    sungNotes: part.sungNotes,
    totalNotes: part.totalNotes,
    ...(part.centDev !== undefined ? { centDev: part.centDev } : {}),
    ...(tendency !== undefined ? { tendency } : {}),
  };
};

/**
 * `accuracy`는 서버 0~1 → 앱 0~100(`toAnalySubmitBody`의 역변환, 그쪽 주석 참고).
 * `low`·`high`가 없으면(서버 실측 표본 중 최소 제출본이 그렇다) `songScore` 전체를
 * 뺀다 — 앱 `SongScore.low`/`.high`는 필수 필드라 반쪽으로 못 채운다("없으면 키를 뺀다").
 */
const toSongScore = (
  song: AnalySongScore | undefined,
): SongScore | undefined => {
  if (
    song?.accuracy === undefined ||
    song.hitRatio === undefined ||
    song.sungNotes === undefined ||
    song.totalNotes === undefined
  ) {
    return undefined;
  }
  const low = toSongPartScore(song.low);
  const high = toSongPartScore(song.high);
  if (!low || !high) {
    return undefined;
  }
  const timing =
    song.timing?.meanOffsetMs !== undefined &&
    song.timing.onTimeRatio !== undefined &&
    song.timing.matchedNotes !== undefined
      ? {
          meanOffsetMs: song.timing.meanOffsetMs,
          onTimeRatio: song.timing.onTimeRatio,
          matchedNotes: song.timing.matchedNotes,
        }
      : undefined;
  return {
    accuracy: song.accuracy * 100,
    hitRatio: song.hitRatio,
    sungNotes: song.sungNotes,
    totalNotes: song.totalNotes,
    low,
    high,
    ...(timing ? { timing } : {}),
  };
};

/** 로컬 프로필 답 중 회차마다 안 변하는 두 키만(CONTEXT.md "Restore"). */
const toSurvey = (
  answers: Record<string, OnboardingAnswer>,
): Record<string, OnboardingAnswer> => {
  const survey: Record<string, OnboardingAnswer> = {};
  for (const key of ['gender', 'birthYear'] as const) {
    if (answers[key] !== undefined) {
      survey[key] = answers[key];
    }
  }
  return survey;
};

export const fromAnalyView = (
  view: AnalyView,
  answers: Record<string, OnboardingAnswer>,
): DiagnosisRecord | null => {
  // 하위 제출(`stereo`가 있음)은 회차가 아니다 — 서버가 목록에 섞어 보낸다(analyTransport 주석).
  if (view.stereo !== undefined && view.stereo !== '') {
    return null;
  }
  if (view.state !== 'done' || view.completedAt === undefined) {
    return null;
  }
  const source = sourceOf(view.algoVersion);
  if (source === 'mock') {
    return null;
  }

  const sustainChild = view.$childs?.find((c) => c.stereo === 'sustain');
  const sustainSec = sustainChild?.metrics$?.sustainSec;
  if (sustainSec === undefined) {
    return null;
  }
  const songChild = view.$childs?.find((c) => c.stereo === 'song');

  const r = view.range$;
  const range: RangeInfo | null =
    r?.lowHz !== undefined &&
    r.highHz !== undefined &&
    r.semitones !== undefined
      ? {
          // 서버 `range$.lowNote`/`highNote`는 MIDI 정수(D37) — 문자열이 필요한 자리라
          // Hz에서 새로 이름을 뽑는다(그 필드를 그대로 옮기지 않는다).
          lowNote: noteName(r.lowHz),
          highNote: noteName(r.highHz),
          lowHz: r.lowHz,
          highHz: r.highHz,
          semitones: r.semitones,
        }
      : null;

  const fach: FachKey | null =
    view.fach !== undefined && FACH_KEYS.includes(view.fach as FachKey)
      ? (view.fach as FachKey)
      : null;

  const metrics = sustainChild?.metrics$;
  const vibrato = toVibrato(metrics?.vibrato);
  const breath = toBreath(metrics?.breath);
  const pitchRegisters = toPitchRegisters(view.registers$);
  const songScore = toSongScore(songChild?.metrics$?.songScore);

  return {
    schemaVersion: DIAGNOSIS_SCHEMA_VERSION,
    id: `d${view.completedAt}`,
    completedAt: view.completedAt,
    source,
    scores: toScores(view.terrain$),
    sustainSec,
    ...(metrics?.hnrDb !== undefined ? { hnrDb: metrics.hnrDb } : {}),
    ...(metrics?.resonanceRatio !== undefined
      ? { resonanceRatio: metrics.resonanceRatio }
      : {}),
    ...(metrics?.resonanceLevelDb !== undefined
      ? { resonanceLevelDb: metrics.resonanceLevelDb }
      : {}),
    ...(metrics?.resonanceCenterHz !== undefined
      ? { resonanceCenterHz: metrics.resonanceCenterHz }
      : {}),
    ...(metrics?.brightnessHz !== undefined
      ? { brightnessHz: metrics.brightnessHz }
      : {}),
    ...(metrics?.bandwidthHz !== undefined
      ? { bandwidthHz: metrics.bandwidthHz }
      : {}),
    ...(vibrato ? { vibrato } : {}),
    ...(breath ? { breath } : {}),
    ...(pitchRegisters ? { pitchRegisters } : {}),
    ...(songScore ? { songScore } : {}),
    range,
    fach,
    survey: toSurvey(answers),
  };
};
